import { a3Copy } from "@/components/assignments/a3-copy";
import { ScaleView } from "./work/scale/Scale";
import type { AssignmentVersion } from "@/lib/api/assignments";

import { useAssignmentDate, assignmentNumber } from "./assignment-workspace-context";
import { deliverableLabel } from "./work/work-facts";

/**
 * The terms learners were given, once they have been corrected. The server
 * returns only terms that reached learners, so a draft's edits never appear.
 * The teaching reason for a correction shows only where `showReason`.
 */
export function AssignmentTermsHistory({
  versions,
  showReason,
}: {
  versions: AssignmentVersion[];
  showReason: boolean;
}) {
  const date = useAssignmentDate();
  if (versions.length < 2) return null;

  const current = versions.at(-1);
  if (!current) return null;
  // Newest first; the first version learners were given has no correction behind it.
  const earlier = versions.slice(0, -1).map((version, index) => ({ version, corrected: index > 0 })).reverse();

  return (
    <section className="grid gap-3 border-t border-rule pt-3 text-ui" aria-labelledby={`terms-history-${current.id}`}>
      <div className="grid gap-1">
        <h3 id={`terms-history-${current.id}`} className="text-body font-semibold text-foreground">{a3Copy.correctedTerms}</h3>
        <p className="text-ui text-muted-foreground">{a3Copy.updated}{date(current.effective_from)}{a3Copy.earlierSubmissionsKeepTheTermsThatAppliedWhenTheyWere}</p>
        {showReason && current.reason && <p className="text-ui text-foreground">{a3Copy.why}{current.reason}</p>}
      </div>
      {earlier.map(({ version, corrected }) => (
        <article key={version.id} className="grid gap-1 border-t border-rule pt-3 text-ui">
          <p className="font-medium text-foreground">{a3Copy.earlierTermsInForceFrom}{date(version.effective_from)}</p>
          <p className="text-foreground [overflow-wrap:anywhere]">{version.title}</p>
          <p className="text-muted-foreground">
            {version.due_at ? a3Copy.due6(date(version.due_at)) : a3Copy.noDueDate}
            {version.points !== null && ` · ${assignmentNumber(version.points)} points`}
            {` · ${deliverableLabel(version.expected_deliverable)}`}
          </p>
          {version.instructions && (
            <p dir="auto" className="whitespace-pre-wrap break-words text-muted-foreground">
              {version.instructions}
            </p>
          )}
          {version.scale && <ScaleView scale={version.scale} />}
          {showReason && corrected && version.reason && <p className="text-muted-foreground">{a3Copy.why}{version.reason}</p>}
        </article>
      ))}
    </section>
  );
}
